"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PoolTable = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const routes_1 = require("../../../../util/routes");
const compliance_pool_types_1 = require("../../compliance-pool-types");
const common_styled_components_1 = require("../../../common-styled-components");
const react_1 = require("react");
const Modal_1 = require("../../common/Modal");
const useEdit_1 = __importDefault(require("../../../../hooks/useEdit"));
const amountUtils_1 = require("../../../../util/amountUtils");
const compliance_1 = require("../../../../util/compliance/compliance");
const useSortedData_1 = require("../../../../hooks/useSortedData");
const NavigationContext_1 = require("../../../../contexts/NavigationContext");
const lodash_1 = require("lodash");
const ExportCsvButton_1 = require("../ExportCsvButton");
const tableColumns = (isInAdminTools) => [
    {
        key: "name",
        label: "Name",
        sortableKey: "name",
    },
    {
        key: "ships",
        label: "Ships",
    },
    {
        key: "intensity",
        label: "Intensity",
        sortableKey: "intensity.adjusted_intensity_gCO2e_per_MJ",
    },
    {
        key: "balance",
        label: "Balance",
        sortableKey: "intensity.adjusted_emission_balance_ton",
    },
    {
        key: "tools",
        label: "Tools",
    },
].filter((t) => t.key !== "tools" || isInAdminTools);
const CellContent = ({ isInAdminTools, pool, contentKey, editPool, compliance, removePool, }) => {
    switch (contentKey) {
        case "name":
            const link = isInAdminTools
                ? `${routes_1.routeNames.admin}/${routes_1.routeNames.poolAdmin}/${pool.id}`
                : `${routes_1.routeNames.pool}/${pool.id}`;
            return ((0, jsx_runtime_1.jsx)(NavigationContext_1.LinkWithHistory, { to: link, children: (0, jsx_runtime_1.jsx)(common_styled_components_1.Bold, { children: pool.name }) }));
        case "ships":
            return (0, jsx_runtime_1.jsx)("div", { children: pool.ships.length });
        case "balance":
            if (pool.intensity) {
                return ((0, jsx_runtime_1.jsxs)("div", { style: { color: compliance_1.complianceOptions[compliance].color }, children: [pool.intensity.adjusted_emission_balance_ton > 0 ? "+" : "", (0, amountUtils_1.formatNumber)(pool.intensity.adjusted_emission_balance_ton, 2)] }));
            }
            else {
                return (0, jsx_runtime_1.jsx)("div", {});
            }
        case "tools":
            return removePool && editPool ? ((0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { onClick: () => removePool(pool), children: "Remove" }), (0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { onClick: () => editPool(pool), children: "Edit" })] })) : ((0, jsx_runtime_1.jsx)("div", {}));
        case "intensity":
            if (pool.intensity) {
                return ((0, jsx_runtime_1.jsxs)("div", { style: { color: compliance_1.complianceOptions[compliance].color }, children: [(0, jsx_runtime_1.jsx)(common_styled_components_1.ComplianceIcon, { src: compliance_1.complianceOptions[compliance].icon }), (0, amountUtils_1.formatNumber)(pool.intensity.adjusted_intensity_gCO2e_per_MJ, 2)] }));
            }
            else {
                return (0, jsx_runtime_1.jsx)("div", {});
            }
        default:
            return (0, jsx_runtime_1.jsx)("div", { children: pool[contentKey] });
    }
};
const PoolTable = ({ pools, isInAdminTools, refresh, }) => {
    const [editStatus, executeEdit] = (0, useEdit_1.default)("admin/pool.json", refresh);
    const [modal, setModal] = (0, react_1.useState)({});
    const { sortedData, sortMethod, sortDirection, setSort } = (0, useSortedData_1.useSortedData)(pools, "id", 1);
    const addPool = () => {
        setModal({
            type: "add",
            value: undefined,
        });
    };
    const editPool = (pool) => {
        setModal({
            type: "edit",
            value: pool,
        });
    };
    const removePool = (pool) => {
        setModal({
            type: "remove",
            value: pool,
        });
    };
    const submit = (type, value) => {
        setModal({});
        const poolInput = (0, lodash_1.pick)(value, compliance_pool_types_1.poolInputKeys);
        if (type === "add" && isInAdminTools) {
            if (poolInput.name &&
                poolInput.name !== "" &&
                poolInput.year !== undefined) {
                const data = {
                    name: poolInput.name,
                    year: Number(poolInput.year),
                    id: null,
                };
                executeEdit(data, "PUT");
            }
            else {
                alert("Empty name");
            }
        }
        if (type === "edit" && isInAdminTools) {
            if (poolInput.name && poolInput.name !== "") {
                const data = {
                    name: poolInput.name,
                    id: poolInput.id ?? null,
                };
                executeEdit(data, "PUT");
            }
            else {
                alert("Empty name");
            }
        }
        if (type === "remove" && isInAdminTools) {
            if (poolInput.id !== undefined) {
                const data = {
                    id: poolInput.id,
                };
                executeEdit(data, "DELETE");
            }
        }
    };
    const columns = tableColumns(isInAdminTools);
    const csvColumns = columns
        .filter((column) => column.key !== "tools")
        .map((column) => ({
        header: column.label,
        getValue: (pool) => {
            switch (column.key) {
                case "name":
                    return pool.name;
                case "ships":
                    return pool.ships.length;
                case "intensity":
                    return pool.intensity
                        ? (0, amountUtils_1.formatNumber)(pool.intensity.adjusted_intensity_gCO2e_per_MJ, 2) ?? ""
                        : "";
                case "balance":
                    return pool.intensity
                        ? `${pool.intensity.adjusted_emission_balance_ton > 0 ? "+" : ""}${(0, amountUtils_1.formatNumber)(pool.intensity.adjusted_emission_balance_ton, 2) ?? ""}`
                        : "";
                default: {
                    const value = pool[String(column.key)];
                    return typeof value === "string" || typeof value === "number"
                        ? value
                        : "";
                }
            }
        },
    }));
    return ((0, jsx_runtime_1.jsxs)("div", { children: [isInAdminTools && ((0, jsx_runtime_1.jsx)(Modal_1.Modal, { type: "pool", isOpen: modal.type !== undefined, closeModal: () => setModal({}), modalType: modal.type, value: modal.value, submit: submit })), (0, jsx_runtime_1.jsx)(common_styled_components_1.TitleWithExtraContent, { titleLevel: "Section", extraContent: (0, jsx_runtime_1.jsxs)(common_styled_components_1.Row, { gap: 1, children: [isInAdminTools && (0, jsx_runtime_1.jsx)(common_styled_components_1.Button, { onClick: addPool, children: "+ Add" }), (0, jsx_runtime_1.jsx)(ExportCsvButton_1.ExportCsvButton, { rows: sortedData, columns: csvColumns, tableName: "pools" })] }), children: "Pools" }), (0, jsx_runtime_1.jsxs)(common_styled_components_1.Table, { children: [(0, jsx_runtime_1.jsx)("thead", { children: (0, jsx_runtime_1.jsx)("tr", { children: columns.map((c) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.SortableTH, { setSort: setSort, sortKey: c.sortableKey, sortDirection: sortMethod && sortMethod === c.sortableKey
                                    ? sortDirection
                                    : undefined, children: c.label }, c.key))) }) }), (0, jsx_runtime_1.jsx)("tbody", { children: sortedData.map((pool) => {
                            const compliance = (0, compliance_1.getCompliance)(pool.intensity.adjusted_emission_balance_ton);
                            return ((0, jsx_runtime_1.jsx)(common_styled_components_1.TR, { children: columns.map((c) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.TD, { children: (0, jsx_runtime_1.jsx)(CellContent, { isInAdminTools: isInAdminTools, contentKey: c.key, pool: pool, removePool: isInAdminTools ? removePool : undefined, editPool: isInAdminTools ? editPool : undefined, compliance: compliance }) }, c.key))) }, pool.id));
                        }) })] })] }));
};
exports.PoolTable = PoolTable;
